import { useMemo } from "react";
import { Code, useComputedColorScheme } from "@mantine/core";
import { highlight, languages } from "prismjs/components/prism-core";

// Register JSON grammar directly — prism-json.js expects a global `Prism` that
// Vite 8 (Rolldown) no longer provides from CJS modules.
if (!languages.json) {
  languages.json = {
    property: { pattern: /(^|[^\\])"(?:\\.|[^\\"\r\n])*"(?=\s*:)/, lookbehind: true, greedy: true },
    string: { pattern: /(^|[^\\])"(?:\\.|[^\\"\r\n])*"(?!\s*:)/, lookbehind: true, greedy: true },
    comment: { pattern: /\/\/.*|\/\*[\s\S]*?(?:\*\/|$)/, greedy: true },
    number: /-?\b\d+(?:\.\d+)?(?:e[+-]?\d+)?\b/i,
    punctuation: /[{}[\],]/,
    operator: /:/,
    boolean: /\b(?:false|true)\b/,
    null: { pattern: /\bnull\b/, alias: "keyword" },
  };
}
import "./prism-vs.css";
import "./prism-vs-dark.css";

/**
 * Renders a value as syntax-highlighted JSON when possible.
 * Accepts a string (will attempt JSON.parse) or an object (will stringify).
 * Falls back to plain <Code block> if the string isn't valid JSON.
 */
export default function JsonBlock({ value, style }) {
  const theme = useComputedColorScheme();
  const themeClass = theme === "dark" ? "rsce-dark" : "rsce-light";

  const { formatted, html } = useMemo(() => {
    let text;
    if (value == null) return { formatted: "", html: null };

    if (typeof value === "string") {
      // Try to parse as JSON; if it fails, render as plain text
      const trimmed = value.trim();
      if (
        (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))
      ) {
        try {
          const parsed = JSON.parse(trimmed);
          text = JSON.stringify(parsed, null, 2);
        } catch {
          return { formatted: value, html: null };
        }
      } else {
        return { formatted: value, html: null };
      }
    } else {
      text = JSON.stringify(value, null, 2);
    }

    const highlighted = highlight(text, languages.json, "json");
    return { formatted: text, html: highlighted };
  }, [value]);

  if (!html) {
    return (
      <Code
        block
        style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", ...style }}
      >
        {formatted}
      </Code>
    );
  }

  return (
    <div className={themeClass}>
      <pre
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          margin: 0,
          padding: "var(--mantine-spacing-xs)",
          fontSize: "var(--mantine-font-size-xs)",
          fontFamily: "var(--mantine-font-family-monospace)",
          borderRadius: "var(--mantine-radius-sm)",
          backgroundColor:
            theme === "dark"
              ? "var(--mantine-color-dark-7)"
              : "var(--mantine-color-gray-0)",
          ...style,
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
